import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { applyTheme, brandLogoUrl } from "../lib/theme";
import { compressImage } from "../lib/imageOptimizer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  GraduationCap, Check, ShieldCheck, Upload, FileText, Trash2, Loader2, IndianRupee, PartyPopper, Download,
} from "lucide-react";
import { toast } from "sonner";

const FILES_ORIGIN = API.replace(/\/api\/?$/, "");
const STEPS = ["Verify", "Academics", "Documents", "Payment"];

const RZP_SRC = "https://checkout.razorpay.com/v1/checkout.js";
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const el = document.querySelector(`script[src="${RZP_SRC}"]`);
    if (el) { el.addEventListener("load", () => resolve(true)); el.addEventListener("error", () => resolve(false)); return; }
    const s = document.createElement("script");
    s.src = RZP_SRC;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function useDraftKey(slug) {
  const host = typeof window !== "undefined" ? window.location.hostname : "host";
  return `edupulse_apply_draft_${slug || host}`;
}

export default function Apply() {
  // No :slug in the route (tenant subdomain root, or bare /apply) => resolve by Host.
  const { slug: routeSlug } = useParams();
  const slug = routeSlug || "";
  const [sp] = useSearchParams();
  const draftKey = useDraftKey(slug);

  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(1);
  const [draftId, setDraftId] = useState(() => {
    try { return localStorage.getItem(draftKey) || ""; } catch { return ""; }
  });
  const [contact, setContact] = useState({ name: "", phone: "", email: "", city: "", state: "" });
  const [otp, setOtp] = useState({ sent: false, code: "", verified: false, hint: "", returning: false, busy: false });
  const [academics, setAcademics] = useState({
    course: "", specialization: "", campus: "",
    tenth: { year: "", board: "", percentage: "" },
    twelfth: { year: "", board: "", percentage: "" },
  });
  const [docs, setDocs] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const persistDraft = useCallback((id) => {
    setDraftId(id);
    try { id ? localStorage.setItem(draftKey, id) : localStorage.removeItem(draftKey); } catch { /* ignore */ }
  }, [draftKey]);

  // resume link from a notification: /apply/:slug?d=<draft_id>
  useEffect(() => {
    const d = sp.get("d");
    if (d && d !== draftId) persistDraft(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  // returning from a redirect-based gateway (Instamojo / CCAvenue)
  useEffect(() => {
    if (sp.get("payment") === "failed") {
      toast.error("Payment was not completed — you can retry.");
      return;
    }
    const imDraft = sp.get("im_draft");
    const imReq = sp.get("payment_request_id");
    const imPay = sp.get("payment_id");
    const ccavDraft = sp.get("ccav_draft");

    if (ccavDraft) {
      // CCAvenue was verified + finalized server-side; just surface the result
      api.get(`/apply/${ccavDraft}`).then((r) => {
        if (r.data.status === "submitted") {
          setResult({ payment: r.data.payment, lead_id: r.data.lead_id,
            draft_id: ccavDraft, receipt_url: `/api/apply/${ccavDraft}/receipt` });
          persistDraft("");
        } else {
          toast.error("We couldn't confirm your CCAvenue payment. Please contact support.");
        }
      }).catch(() => toast.error("We couldn't confirm your payment."));
      return;
    }
    if (imDraft && imReq && imPay) {
      setBusy(true);
      api.post(`/apply/${imDraft}/instamojo/verify`, { payment_id: imPay, payment_request_id: imReq })
        .then((r) => { setResult(r.data); persistDraft(""); toast.success("Payment approved — application submitted"); })
        .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
        .finally(() => setBusy(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // load institution config
  useEffect(() => {
    const use = (r) => {
      setConfig(r.data);
      const inst = r.data.institution || {};
      applyTheme({ branding: inst.branding, title: inst.header_title || `${inst.name} — Apply` });
    };
    api.get(slug ? `/apply/${slug}/config` : "/apply/config")
      .then(use)
      .catch(() => toast.error("Could not load the application form"));
  }, [slug]);

  // resume an existing draft
  useEffect(() => {
    if (!draftId) return;
    // a gateway return is being handled by the effect above — don't stomp it
    if (sp.get("im_draft") || sp.get("ccav_draft")) return;
    api.get(`/apply/${draftId}`).then((r) => {
      const d = r.data;
      if (d.status === "submitted") { persistDraft(""); return; }
      if (d.contact?.name) setContact((c) => ({ ...c, ...d.contact }));
      if (d.otp_verified) setOtp((o) => ({ ...o, verified: true, sent: true }));
      if (d.academics) { setAcademics((a) => ({ ...a, ...d.academics })); }
      if (d.documents) setDocs(d.documents);
      let s = 1;
      if (d.otp_verified) s = 2;
      if (d.academics) s = 3;
      if (d.documents && Object.keys(d.documents).length >= 3) s = 4;
      setStep(s);
    }).catch(() => persistDraft(""));
  }, [draftId, persistDraft]);

  const specs = useMemo(
    () => config?.courses?.find((c) => c.name === academics.course)?.specializations || [],
    [config, academics.course]
  );

  // ---- step 1 ----
  const startAndSend = async () => {
    setOtp((o) => ({ ...o, busy: true }));
    try {
      let id = draftId;
      if (!id) {
        const { data } = await api.post("/apply/start", {
          institution_slug: config?.institution?.slug || slug || "",
          ...contact,
        });
        id = data.draft_id;
        persistDraft(id);
      }
      const { data } = await api.post(`/apply/${id}/otp/send`);
      setOtp((o) => ({ ...o, sent: true, hint: data.mock_otp, busy: false }));
      toast.success("Verification code sent");
    } catch (e) {
      setOtp((o) => ({ ...o, busy: false }));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const verifyOtp = async () => {
    setOtp((o) => ({ ...o, busy: true }));
    try {
      const { data } = await api.post(`/apply/${draftId}/otp/verify`, { code: otp.code });
      setOtp((o) => ({ ...o, verified: true, returning: !!data.returning, busy: false }));
      toast.success("Mobile verified");
    } catch (e) {
      setOtp((o) => ({ ...o, busy: false }));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const changeDetails = () => {
    persistDraft("");
    setOtp({ sent: false, code: "", verified: false, hint: "", returning: false, busy: false });
    setStep(1);
  };

  // ---- step 2 ----
  const saveAcademics = async () => {
    setBusy(true);
    try {
      await api.post(`/apply/${draftId}/academics`, {
        course: academics.course, specialization: academics.specialization, campus: academics.campus,
        tenth: academics.tenth, twelfth: academics.twelfth,
      });
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // ---- step 3 ----
  const uploadDoc = async (docType, file) => {
    if (!file) return;
    const optimized = await compressImage(file);
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("file", optimized);
    try {
      const { data } = await api.post(`/apply/${draftId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => ({ ...d, [docType]: data }));
      toast.success(`${data.label} uploaded`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const removeDoc = async (docType) => {
    try {
      await api.delete(`/apply/${draftId}/documents/${docType}`);
      setDocs((d) => { const n = { ...d }; delete n[docType]; return n; });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- step 4 ----
  const [method, setMethod] = useState("UPI");

  const finishSubmit = (data) => {
    setResult(data);
    persistDraft("");
    toast.success("Payment approved — application submitted");
  };

  const payWithRazorpay = async () => {
    const ok = await loadRazorpayScript();
    if (!ok) { toast.error("Could not reach the payment gateway. Please retry."); return; }
    const { data: order } = await api.post(`/apply/${draftId}/razorpay/order`);
    return new Promise((resolve) => {
      const rzp = new window.Razorpay({
        key: order.key_id, // institution-specific key from institutions.integrations.razorpay
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency,
        name: config?.institution?.name || "Application fee",
        description: "Application fee",
        theme: { color: config?.institution?.branding?.primary_color || "#1E40AF" },
        handler: async (resp) => {
          try {
            const { data } = await api.post(`/apply/${draftId}/razorpay/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              method: "Razorpay",
            });
            finishSubmit(data);
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
          } finally { resolve(); }
        },
        modal: { ondismiss: () => resolve() },
      });
      rzp.open();
    });
  };

  const payWithInstamojo = async () => {
    const { data } = await api.post(`/apply/${draftId}/instamojo/order`);
    if (!data.checkout_url) { toast.error("Could not start the Instamojo checkout"); return; }
    window.location.href = data.checkout_url; // returns to /apply/:slug?im_draft=…
  };

  const payWithCCAvenue = async () => {
    const { data } = await api.post(`/apply/${draftId}/ccavenue/order`);
    const form = document.createElement("form");
    form.method = "POST";
    form.action = data.action_url;
    [["encRequest", data.enc_request], ["access_code", data.access_code]].forEach(([n, v]) => {
      const i = document.createElement("input");
      i.type = "hidden"; i.name = n; i.value = v; form.appendChild(i);
    });
    document.body.appendChild(form);
    form.submit(); // CCAvenue posts back to /api/apply/:draft/ccavenue/callback
  };

  const pay = async () => {
    setBusy(true);
    try {
      const prov = config?.payment?.provider;
      if (prov === "razorpay") await payWithRazorpay();
      else if (prov === "instamojo") await payWithInstamojo();
      else if (prov === "ccavenue") await payWithCCAvenue();
      else {
        const { data } = await api.post(`/apply/${draftId}/submit`, { method });
        finishSubmit(data);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!config) {
    return <div className="min-h-screen grid place-items-center bg-slate-50">
      <Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const fee = config.fee;
  const allDocs = (config.doc_types || []).every((dt) => docs[dt.key]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="apply-portal">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          {brandLogoUrl(config.institution.branding) ? (
            <img src={brandLogoUrl(config.institution.branding)} alt={config.institution.name}
              className="h-10 w-10 rounded-lg object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling.style.display = "flex"; }} />
          ) : null}
          <div className="w-10 h-10 rounded-lg brand-fill items-center justify-center"
            style={{ display: brandLogoUrl(config.institution.branding) ? "none" : "flex" }}>
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-slate-900 leading-tight">{config.institution.name}</div>
            <div className="text-xs text-slate-500">{config.institution.header_title || "Online Application"}</div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {result ? (
          <SuccessCard result={result} draftId={result.draft_id} onRestart={() => { setResult(null); setStep(1); setContact({ name: "", phone: "", email: "", city: "", state: "" }); setOtp({ sent: false, code: "", verified: false, hint: "", returning: false, busy: false }); setAcademics({ course: "", specialization: "", campus: "", tenth: { year: "", board: "", percentage: "" }, twelfth: { year: "", board: "", percentage: "" } }); setDocs({}); }} />
        ) : (
          <>
            <Stepper step={step} />
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 sm:p-6 mt-4">
              {step === 1 && (
                <Step1
                  contact={contact} setContact={setContact} otp={otp} setOtp={setOtp}
                  locked={!!draftId} onSend={startAndSend} onVerify={verifyOtp} onChangeDetails={changeDetails}
                />
              )}
              {step === 2 && (
                <Step2 config={config} academics={academics} setAcademics={setAcademics} specs={specs} />
              )}
              {step === 3 && (
                <Step3 config={config} docs={docs} onUpload={uploadDoc} onRemove={removeDoc} draftId={draftId} />
              )}
              {step === 4 && (
                <Step4 fee={fee} method={method} setMethod={setMethod} payment={config.payment} />
              )}

              <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
                <Button variant="ghost" disabled={step === 1 || busy} onClick={() => setStep((s) => s - 1)} data-testid="apply-back">Back</Button>
                {step < 4 ? (
                  <Button
                    data-testid="apply-next"
                    className="bg-brand"
                    disabled={
                      busy ||
                      (step === 1 && !otp.verified) ||
                      (step === 2 && !(academics.course && academics.campus)) ||
                      (step === 3 && !allDocs)
                    }
                    onClick={() => {
                      if (step === 2) saveAcademics();
                      else setStep((s) => s + 1);
                    }}
                  >
                    {busy ? "…" : "Continue"}
                  </Button>
                ) : (
                  <Button data-testid="apply-pay" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={pay}>
                    {busy ? "Processing…" : `Pay ₹${fee.total.toLocaleString("en-IN")}${GATEWAY_LABEL[config.payment?.provider] ? ` with ${GATEWAY_LABEL[config.payment.provider]}` : ""}`}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <ol className="flex items-center" data-testid="apply-stepper">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const done = n < step, active = n === step;
        return (
          <li key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold ${
                done ? "bg-emerald-600 text-white" : active ? "brand-active" : "bg-slate-200 text-slate-500"}`}>
                {done ? <Check className="w-4 h-4" /> : n}
              </div>
              <span className={`text-[11px] mt-1 ${active ? "text-slate-800 font-medium" : "text-slate-400"}`}>{label}</span>
            </div>
            {n < STEPS.length && <div className={`h-0.5 flex-1 mx-2 ${done ? "bg-emerald-600" : "bg-slate-200"}`} />}
          </li>
        );
      })}
    </ol>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

function Step1({ contact, setContact, otp, setOtp, locked, onSend, onVerify, onChangeDetails }) {
  const set = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));
  return (
    <div className="space-y-4" data-testid="apply-step-1">
      <h2 className="font-display text-lg font-bold text-slate-900">Basic information</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name"><Input data-testid="apply-name" value={contact.name} onChange={set("name")} disabled={locked} /></Field>
        <Field label="Mobile number"><Input data-testid="apply-phone" value={contact.phone} onChange={set("phone")} disabled={locked} inputMode="numeric" placeholder="10-digit" /></Field>
        <Field label="Email"><Input data-testid="apply-email" type="email" value={contact.email} onChange={set("email")} disabled={locked} /></Field>
        <Field label="City"><Input data-testid="apply-city" value={contact.city} onChange={set("city")} disabled={locked} /></Field>
        <Field label="State"><Input data-testid="apply-state" value={contact.state} onChange={set("state")} disabled={locked} /></Field>
      </div>

      {!otp.verified ? (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-3">
          {!otp.sent ? (
            <Button data-testid="apply-send-otp" onClick={onSend} disabled={otp.busy || !(contact.name && contact.phone && contact.email && contact.state)}
              className="bg-brand">
              {otp.busy ? "Sending…" : "Send verification code"}
            </Button>
          ) : (
            <>
              <div className="flex items-end gap-3">
                <Field label="6-digit code">
                  <Input data-testid="apply-otp-input" value={otp.code} inputMode="numeric" maxLength={6}
                    onChange={(e) => setOtp((o) => ({ ...o, code: e.target.value.replace(/\D/g, "") }))} className="w-40 tracking-[0.4em] font-mono" />
                </Field>
                <Button data-testid="apply-verify-otp" onClick={onVerify} disabled={otp.busy || otp.code.length !== 6}
                  className="bg-brand">Verify</Button>
                <Button variant="ghost" size="sm" onClick={onSend} disabled={otp.busy}>Resend</Button>
              </div>
              {otp.hint && <p className="text-xs text-slate-500">Demo mode — your code is <span className="font-mono font-semibold">{otp.hint}</span></p>}
            </>
          )}
          {locked && <button type="button" onClick={onChangeDetails} className="text-xs text-slate-500 underline">Change my details</button>}
        </div>
      ) : (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2 text-sm text-emerald-800" data-testid="apply-verified">
          <ShieldCheck className="w-4 h-4" /> Mobile verified.
          {otp.returning && <span className="ml-1">Welcome back — we found your existing record and will link this application to it.</span>}
        </div>
      )}
    </div>
  );
}

function Step2({ config, academics, setAcademics, specs }) {
  const setMarks = (level, k) => (e) =>
    setAcademics((a) => ({ ...a, [level]: { ...a[level], [k]: e.target.value } }));
  return (
    <div className="space-y-5" data-testid="apply-step-2">
      <h2 className="font-display text-lg font-bold text-slate-900">Academic details & program</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Program / Degree">
          <Select value={academics.course} onValueChange={(v) => setAcademics((a) => ({ ...a, course: v, specialization: "" }))}>
            <SelectTrigger data-testid="apply-course"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{config.courses.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Specialization">
          <Select value={academics.specialization} onValueChange={(v) => setAcademics((a) => ({ ...a, specialization: v }))} disabled={!specs.length}>
            <SelectTrigger data-testid="apply-specialization"><SelectValue placeholder={specs.length ? "Select" : "—"} /></SelectTrigger>
            <SelectContent>{specs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Preferred campus">
          <Select value={academics.campus} onValueChange={(v) => setAcademics((a) => ({ ...a, campus: v }))}>
            <SelectTrigger data-testid="apply-campus"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{config.campuses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      {[["tenth", "Class 10"], ["twelfth", "Class 12 / Graduation"]].map(([level, title]) => (
        <div key={level}>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{title}</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Passing year"><Input data-testid={`apply-${level}-year`} value={academics[level].year} onChange={setMarks(level, "year")} inputMode="numeric" /></Field>
            <Field label="Board / University"><Input data-testid={`apply-${level}-board`} value={academics[level].board} onChange={setMarks(level, "board")} /></Field>
            <Field label="Percentage / CGPA"><Input data-testid={`apply-${level}-pct`} value={academics[level].percentage} onChange={setMarks(level, "percentage")} /></Field>
          </div>
        </div>
      ))}
    </div>
  );
}

function Step3({ config, docs, onUpload, onRemove, draftId }) {
  return (
    <div className="space-y-4" data-testid="apply-step-3">
      <h2 className="font-display text-lg font-bold text-slate-900">Document locker</h2>
      <p className="text-sm text-slate-500">PDF, JPEG or PNG · up to {config.max_upload_mb} MB each.</p>
      {(config.doc_types || []).map((dt) => {
        const d = docs[dt.key];
        return (
          <div key={dt.key} className="rounded-lg border border-slate-200 p-3" data-testid={`apply-doc-${dt.key}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{dt.label}</div>
                {d ? (
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                    <FileText className="w-3.5 h-3.5 shrink-0" />
                    <a href={`${FILES_ORIGIN}/api/apply/${draftId}/documents/${dt.key}`} target="_blank" rel="noreferrer"
                      className="truncate underline">{d.filename}</a>
                    <Badge className="bg-amber-100 text-amber-800 shrink-0">Pending Verification</Badge>
                  </div>
                ) : <div className="text-xs text-slate-400 mt-1">Not uploaded</div>}
              </div>
              <div className="shrink-0">
                {d ? (
                  <Button variant="ghost" size="icon" data-testid={`apply-doc-remove-${dt.key}`} onClick={() => onRemove(dt.key)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                ) : (
                  <label className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-brand cursor-pointer">
                    <Upload className="w-3.5 h-3.5" /> Upload
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                      data-testid={`apply-doc-input-${dt.key}`}
                      onChange={(e) => onUpload(dt.key, e.target.files?.[0])} />
                  </label>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const GATEWAY_LABEL = { razorpay: "Razorpay", instamojo: "Instamojo", ccavenue: "CCAvenue" };

function Step4({ fee, method, setMethod, payment }) {
  const prov = payment?.provider;
  const hosted = prov === "razorpay" || prov === "instamojo" || prov === "ccavenue";
  const sandbox = payment?.instamojo_sandbox || payment?.ccavenue_sandbox;
  return (
    <div className="space-y-4" data-testid="apply-step-4">
      <h2 className="font-display text-lg font-bold text-slate-900">Application fee</h2>
      <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm">
        <Row k="Application fee" v={fee.base} />
        <Row k={`GST @ ${Math.round(fee.gst_rate * 100)}%`} v={fee.gst} />
        <div className="flex items-center justify-between p-3 font-semibold text-slate-900">
          <span>Total payable</span>
          <span className="font-mono inline-flex items-center"><IndianRupee className="w-4 h-4" />{fee.total.toLocaleString("en-IN")}</span>
        </div>
      </div>
      {hosted ? (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm" data-testid="apply-gateway-note">
          <div className="font-medium text-slate-700">
            Secure checkout by {GATEWAY_LABEL[prov]}{sandbox ? " (test mode)" : ""}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {prov === "razorpay" && <>Key <span className="font-mono">{payment.razorpay_key_id || "—"}</span> · </>}
            {payment.key_source === "institution" ? "this institution's gateway" : "platform default"}
          </div>
        </div>
      ) : (
        <>
          <Field label="Payment method">
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="apply-method" className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>{["UPI", "Card", "NetBanking"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <p className="text-xs text-slate-400">Mock gateway — no real charge. A GST invoice is generated on success.</p>
        </>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between p-3">
      <span className="text-slate-600">{k}</span>
      <span className="font-mono">₹{v.toLocaleString("en-IN")}</span>
    </div>
  );
}

function SuccessCard({ result, onRestart }) {
  const receipt = `${FILES_ORIGIN}${result.receipt_url}`;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 text-center" data-testid="apply-success">
      <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto">
        <PartyPopper className="w-7 h-7 text-emerald-600" />
      </div>
      <h2 className="font-display text-xl font-bold text-slate-900 mt-3">Application submitted</h2>
      <p className="text-sm text-slate-500 mt-1">Your payment was approved and your application is now with our admissions team.</p>
      <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-left inline-block">
        {result.application_no && (
          <div><span className="text-slate-500">Application No:</span> <span className="font-mono">{result.application_no}</span></div>
        )}
        <div><span className="text-slate-500">Application ID:</span> <span className="font-mono">{result.application_id}</span></div>
        <div><span className="text-slate-500">Txn:</span> <span className="font-mono">{result.payment?.txn_id}</span></div>
        <div><span className="text-slate-500">Amount:</span> ₹{result.payment?.total_amount?.toLocaleString("en-IN")}</div>
      </div>
      {result.tracking_url && (
        <p className="text-xs text-slate-400 mt-3">
          Bookmark this link to track your application anytime:{" "}
          <a href={result.tracking_url} className="underline text-slate-600" data-testid="apply-tracking-link">
            {FILES_ORIGIN}{result.tracking_url}
          </a>
        </p>
      )}
      <div className="flex flex-col sm:flex-row gap-2 justify-center mt-5">
        <a href={receipt} target="_blank" rel="noreferrer">
          <Button className="bg-brand w-full sm:w-auto" data-testid="apply-download-receipt">
            <Download className="w-4 h-4 mr-1.5" /> Download GST receipt
          </Button>
        </a>
        {result.tracking_url && (
          <a href={result.tracking_url}>
            <Button variant="outline" className="w-full sm:w-auto" data-testid="apply-track-button">Track Application</Button>
          </a>
        )}
        <Button variant="outline" onClick={onRestart}>Start another application</Button>
      </div>
    </div>
  );
}
